import _ from 'lodash';
import Scorm2004CMIModel from './Scorm2004CMIModel';
import { encode, decode } from 'msgpack-lite';

const ScormEvents = { 
    INITIALIZE: 'initialize', 
    CHANGE: 'change', 
    COMMIT: 'commit', 
    FINISH: 'finish' 
};

export class ScormRuntime2004 {

    /********************************************
     * Constructor
     * 
     * 'callbacks' can be one or all of:
     * initialize, change, commit, finish, error
     ********************************************/
    
    constructor( { CMIModel, debug = false, studentName = '', studentId = '', localeCode = '', eventCallbacks = {} } ) {
        this.debug = debug;
        this.localeCode = localeCode; 
        
        const newModel = JSON.parse( JSON.stringify( Scorm2004CMIModel ) );
        
        newModel.cmi.learner_name = studentName;
        newModel.cmi.learner_id = studentId;
        
        // If CMI model doesn't exist or is blank
        if ( !CMIModel || Object.keys( CMIModel ).length === 0 ) {
            this.model = Scorm2004CMIModel;
        } else {
            this.model = CMIModel;    
        }

        this.eventCallbacks = eventCallbacks;
    }
   
    /**************************
     * SCORM Events
     **************************/
    
    Initialize ( arg ) {
        this.debug && console.log( 'Initializing Scorm Runtime 2004', arg );
        this.debug && this.logChamelonVersion() ; 
        this.debug && console.log( 'Initial scorm_state:', this.model ); 
        this.debug && console.log( 'Initial suspend_data:', this.decodeSuspendData( this.model ) ); 
                                    
        //this.updateSuspendData( this.model, this.localeCode );

        this.triggerCallback( ScormEvents.INITIALIZE );
        
        return true;
    }

    Terminate ( arg ) {
        //this.debug && console.log( 'Terminate:', arg );
        
        this.triggerCallback( ScormEvents.FINISH );
        
        return true;
    }

    Commit ( arg ) {
        //this.debug && console.log( 'Commit:', arg );
        
        this.triggerCallback( ScormEvents.COMMIT );
        
        return true;
    }
        
    SetValue ( element, value ) {
        //this.debug && console.log( 'SetValue:', element, value );

        const result = this.setCMIValue( element, value );
        this.triggerCallback( ScormEvents.CHANGE );

        return result;
    }
    
    GetValue ( element ) { 
        //this.debug && console.log( `GetValue: ${ element }: ${ this.getCMIValue( element ) }` );
        
        return this.getCMIValue( element );
    }

    GetLastError() {
        // GetLastError() : CMIErrorCode – Returns the error code that resulted from the last API call.
        // TODO: Saving this for later useage
    }

    GetErrorString( errorCode ) {
        // GetErrorString( errorCode : CMIErrorCode ) : string – Returns a short string describing the specified error code.
        // TODO: Saving this for later useage
    }

    GetDiagnostic( errorCode ) {
        // GetDiagnostic( errorCode : CMIErrorCode ) : string – Returns detailed information about the last error that occurred.
        // TODO: Saving this for later useage
    }

    /**************************
     * Event handlers
     **************************/
   
    triggerCallback( eventName ) {
        if ( !this.eventCallbacks[ eventName ] ) {
            this.debug && console.log( `Event callback ${ eventName } does not exist` );
            return;
        }

        this.eventCallbacks[ eventName ]( eventName, this.model );
    }

    /**************************
     * Handle suspend_data
     **************************/
    
    hexToBuffer( data = '' ) {
        try {
            return data.split( /\s+/ ).filter( ( chr ) => {
                return ( chr !== '' );
            } ).map( ( chr ) => {
                return parseInt( chr, 16 );
            } );
        } catch ( e ) {
            return [];
        }
    }
                   
    bufferToHex( buffer = [] ) {
        try {
            return Array.prototype.map.call( buffer, ( val ) => {
                let hex = ( val ).toString( 16 ).toUpperCase();
                
                if ( val < 16 ) {
                    hex = '0' + hex;
                }

                return hex;
            } ).join( ' ' );
        } catch ( e ) {
            return [];
        }
    }

    updateSuspendData( model, localeCode ) {
        if ( !model?.cmi?.suspend_data || !localeCode ) { 
            return;
        }

        const buffer = this.hexToBuffer( model.cmi.suspend_data );
                    
        if ( !buffer || buffer.length === 0 ) { 
            return;
        }

        let decoded;

        try {
            decoded = decode( buffer );
        } catch ( e ) {
            decoded = {};
        }

        if ( !decoded.lang ) { 
            return;
        }

        if ( localeCode === 'USA' && decoded.lang === 'en-US' ) {
            return;
        }

        if ( decoded.lang !== localeCode  ) {
            if ( !this.languageExistsForLocale( localeCode ) ) {
                if ( localeCode === 'USA' && this.languageExistsForLocale( 'en-US' ) ) {
                    localeCode = 'en-US';
                } else {
                    console.log( `Cannot change to ${ localeCode } because it does not exist in this course.` );

                    if ( this.languageExistsForLocale( 'en-US' ) ) {
                        console.log( 'Changing to default locale en-US' );
                        localeCode = 'en-US';
                    } else {
                        return;
                    }
                }
            }
        }

        if ( decoded.lang !== localeCode  ) {
            decoded.lang = localeCode;

            try {
                const newBuffer = encode( decoded );
                const encoded = this.bufferToHex( newBuffer );

                model.cmi.suspend_data = encoded;

                this.debug && console.log( `Changing suspend_data locale to: ${ localeCode }` ); 
                this.debug && console.log( 'Modified suspend_data:', this.decodeSuspendData( model ) ); 
            } catch ( e ) { 
                this.debug && console.error( 'Error modifying suspend_data' ); 
            }
        }
    }

    decodeSuspendData( model ) {
        if ( !model?.cmi?.suspend_data ) { 
            return '';
        }

        const buffer = this.hexToBuffer( model.cmi.suspend_data );
                    
        if ( !buffer || buffer.length === 0 ) { 
            return '';
        }
        
        try {
            let decoded = decode( buffer );
            
            if ( !decoded ) { 
                return '';
            }
                    
            return decoded;
        } catch ( e ) {
            return '';
        }
    }

    /**************************
     * Utility Functions
     **************************/
    
    getCMIValue ( keyPath ) {
        const searchKeys = keyPath.split( '.' );
        let index = 0;

        function getKey( JSObject ) {
            const fkey = JSObject[ searchKeys[ index ] ] ;
     
            if ( fkey === undefined ) {
                return undefined;
            }

            if ( typeof fkey === 'object' && fkey !== null && !Array.isArray( fkey ) && index < searchKeys.length - 1 ) {
                index++;
                return getKey( fkey );
            } else {
                return fkey;
            }
        } 
        
        const result = getKey( this.model );

        return result || '';
    }   
 
    setCMIValue( keyPath, newValue ) {
        const searchKeys = keyPath.split( '.' );
        let index = 0;

        function getKey( JSObject ) {
            let fkey = JSObject[ searchKeys[ index ] ] ;
     
            if ( fkey === undefined ) {
                return undefined;
            }

            if ( typeof fkey === 'object' && fkey !== null && !Array.isArray( fkey ) && index < searchKeys.length - 1 ) {
                index++;
                return getKey( fkey );
            } else {
                JSObject[ searchKeys[ index ] ]  = newValue;
                return newValue;
            }
        } 
       
        return getKey( this.model );       
    } 

    getLanguages() {
        return _.concat( 
            window.courseWindow?.data?.config?.languageSet || [], 
            window.courseWindow?.data?.config?.languages || [] );
    }

    languageExistsForLocale( locale ) {
        return this.getLanguages().find( i => locale === i.shortcode ) ? true : false;
    }

    logChamelonVersion() {
        let chameleonVersion = 'unknown';
        let scormVersion = window.courseWindow?.api || 'unknown';
        const baseURL = window.courseWindow?.baseURL || '';
        
        if ( baseURL.startsWith( 'https://chameleon-4-prod' ) ) {
            chameleonVersion =  'Chameleon 4 PROD';
        }

        if ( baseURL.startsWith( 'https://chameleon-4-pprd' ) ) {
            chameleonVersion =  'Chameleon 4 PPRD';
        }

        if ( baseURL.startsWith( 'https://chameleon-4-qa' ) ) {
            chameleonVersion =  'Chameleon 4 QA';
        }

        if ( baseURL.startsWith( 'https://chameleon-3-prod' ) ) {
            chameleonVersion =  'Chameleon 3 PROD';
        }

        if ( baseURL.startsWith( 'https://chameleon-3-pprd' ) ) {
            chameleonVersion =  'Chameleon 3 PPRD';
        }

        if ( baseURL.startsWith( 'https://chameleon-3-qa' ) ) {
            chameleonVersion =  'Chameleon 3 QA';
        }
                    
        console.log( `Course Version: ${ chameleonVersion }` );
        const languages = window.courseWindow?.data?.config?.languageSet;
                    
        if ( languages ) {
            console.log( 'Course languages:', languages );
        }
                    
        console.log( `SCORM Version: ${ scormVersion }` );            
    }
}
